/**
 * Diagnostics — detailed troubleshooting screen (doc 36).
 *
 * Merchant-facing health overview. Connection and browser-integration lanes
 * intentionally stay out of this page because they produced false negatives
 * in embedded contexts. System/runtime information is rendered separately by
 * DiagnosticsSnapshotPanel after the event timeline.
 */

import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@material-ui/core';
import {
  CheckCircle,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  FiberManualRecord,
} from '@material-ui/icons';
import clsx from 'clsx';
import {
  IndexingStatus,
  PreviewBuildStatus,
  PreviewBrowserStatus,
  LiveStatus,
  INDEXING_STALE_MS,
} from '../../operatorState';
import { useLocalizedMessage } from '../../../../util/useLocalizedMessage';
import { usePreviewOpener } from '../../../shell/preview/usePreviewOpener';
import BundleBuildBlock from './BundleBuildBlock';
import styles from './Diagnostics.module.scss';

const CAPABILITY_LABELS = Object.freeze({
  operatorStateV1: 'Search health state',
  indexProgressV1: 'Indexing progress',
  onboardingCheckpointsV1: 'Setup checkpoints',
  catalogSelectorsV1: 'Product selection rules',
  availabilityRulesV1: 'Availability rules',
  payloadProvidersV1: 'Product data payload',
  variantPresentationSettingsV1: 'Variation presentation',
  variantGroupingV1: 'Variant grouping',
  diagnosticEventsV1: 'Diagnostic events',
  diagnosticSnapshotV1: 'System snapshot',
  publishStateV1: 'Publish status',
  liveEnabledV1: 'Live search control',
});

// ---------------------------------------------------------------------------
// Exported helpers (unit-tested)
// ---------------------------------------------------------------------------

export function countIssues(s) {
  let errors = 0;
  let warnings = 0;
  if (s.indexing === IndexingStatus.ERROR) errors++;
  if (s.previewBuild === PreviewBuildStatus.ERROR) errors++;
  if (s.previewBrowser === PreviewBrowserStatus.ERROR) errors++;
  if (s.previewBrowser === PreviewBrowserStatus.STALE) warnings++;
  if (s.live === LiveStatus.ERROR) errors++;
  return { errors, warnings };
}

export function isIndexingStuck(pluginSnapshot, now) {
  if (!pluginSnapshot?.available) return false;
  const { status, updatedAt } = pluginSnapshot.indexing || {};
  if (status !== 'running' && status !== 'queued') return false;
  if (!updatedAt) return false;
  const updatedMs = new Date(updatedAt).getTime();
  if (Number.isNaN(updatedMs)) return false;
  return ((now || Date.now()) - updatedMs) > INDEXING_STALE_MS;
}

export function hasUnverifiedChecks(s) {
  return countUnverifiedAreas(s) > 0;
}

export function countUnverifiedAreas(s) {
  return [
    s.indexing !== IndexingStatus.READY && s.indexing !== IndexingStatus.ERROR,
    (s.previewBuild !== PreviewBuildStatus.READY && s.previewBuild !== PreviewBuildStatus.ERROR)
      || s.previewBrowser === PreviewBrowserStatus.UNKNOWN,
  ].filter(Boolean).length;
}

export function buildSystemInfoText(pluginSnapshot, browserLocalLane, operatorState) {
  const lines = ['--- Kea Search Diagnostics ---'];
  const ctx = pluginSnapshot?.context || {};
  lines.push(`Plugin version: ${ctx.moduleVersion || 'unknown'}`);
  lines.push(`Platform: ${ctx.platform || 'unknown'}`);
  if (pluginSnapshot?.capabilities) {
    const caps = Object.entries(pluginSnapshot.capabilities)
      .filter(([, v]) => v).map(([k]) => CAPABILITY_LABELS[k] || k);
    lines.push(`Capabilities: ${caps.length ? caps.join(', ') : 'none'}`);
  }
  lines.push(`localStorage: ${browserLocalLane?.available ? 'available' : 'unavailable'}`);
  lines.push(`Connection: ${operatorState.connection}`);
  lines.push(`Indexing: ${operatorState.indexing}`);
  lines.push(`Mode: ${operatorState.mode || 'none'}`);
  lines.push(`Preview build: ${operatorState.previewBuild}`);
  lines.push(`Preview browser: ${operatorState.previewBrowser}`);
  lines.push(`Live: ${operatorState.live}`);
  lines.push(`Integration: ${operatorState.integrationHealth}`);
  lines.push(`Severity: ${operatorState.diagnosticsSeverity}`);
  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// Severity icon helper
// ---------------------------------------------------------------------------

function sevIcon(severity) {
  switch (severity) {
    case 'error': return <ErrorIcon className={clsx(styles.iconSize, styles.iconErr)} />;
    case 'warning': return <WarningIcon className={clsx(styles.iconSize, styles.iconWarn)} />;
    case 'ok': return <CheckCircle className={clsx(styles.iconSize, styles.iconOk)} />;
    case 'info': return <InfoIcon className={clsx(styles.iconSize, styles.iconInfo)} />;
    default: return <FiberManualRecord className={clsx(styles.iconSize, styles.iconNeutral)} />;
  }
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function Diagnostics({
  operatorState,
  pluginSnapshot,
  browserLocalLane,
  shopId,
  isLoading,
  onStartReindex,
  onRebuildBundle,
  onBuildAndPromote,
  bundleRebuildStatus,
  bundleRebuildError,
}) {
  const localizeMessage = useLocalizedMessage();
  const { previewShopAddress, openStorefrontPreview } = usePreviewOpener();
  const s = operatorState;

  const handleOpenPreview = useCallback(() => {
    if (previewShopAddress) openStorefrontPreview();
  }, [openStorefrontPreview, previewShopAddress]);

  // --- Loading state ---
  if (isLoading) {
    return (
      <div className={styles.root} data-testid="diagnostics-loading">
        <div className={styles.skeleton} style={{ height: 40 }} />
        <div className={styles.skeleton} style={{ height: 120 }} />
        <div className={styles.skeleton} style={{ height: 120 }} />
      </div>
    );
  }

  // --- Derived values ---
  const issues = countIssues(s);
  const hasIssues = issues.errors > 0 || issues.warnings > 0;
  const pendingChecks = countUnverifiedAreas(s);
  const hasUnverified = !hasIssues && pendingChecks > 0;
  // Known limitation: `stuck` is computed once per render. Polling drives the
  // next evaluation when indexing changes after the page loads.
  const stuck = isIndexingStuck(pluginSnapshot);
  const plugin = pluginSnapshot || {};
  const feedback = browserLocalLane?.previewFeedback || null;

  return (
    <div className={styles.root} data-testid="diagnostics">
      {/* ============================================================ */}
      {/* 1. Summary / Severity (§4.2)                                 */}
      {/* ============================================================ */}
      <section
        className={clsx(
          styles.summaryHero,
          issues.errors > 0 && styles.summaryError,
          issues.errors === 0 && issues.warnings > 0 && styles.summaryWarning,
          hasUnverified && styles.summaryUnverified,
        )}
        data-testid="diag-summary"
      >
        <div className={styles.summaryContent}>
          <div className={styles.summaryKicker}>
            {sevIcon(hasIssues ? (issues.errors > 0 ? 'error' : 'warning') : hasUnverified ? 'info' : 'ok')}
            <span>{localizeMessage('operator.diag.summary.title')}</span>
          </div>
          <h2
            className={styles.summaryTitle}
            data-testid={!hasIssues && !hasUnverified ? 'diag-summary-healthy' : undefined}
          >
            {hasIssues
              ? localizeMessage('operator.diag.summary.needs_attention')
              : hasUnverified
                ? localizeMessage('operator.diag.summary.no_active_problems')
                : localizeMessage('operator.diag.summary.all_healthy')}
          </h2>
          <p className={styles.summaryDescription}>
            {hasIssues
              ? localizeMessage('operator.diag.summary.review_highlighted')
              : hasUnverified
                ? localizeMessage('operator.diag.summary.unverified_hint')
                : localizeMessage('operator.diag.summary.healthy_hint')}
          </p>
        </div>
        <div className={styles.summaryStats} aria-label={localizeMessage('operator.diag.summary.countsAriaLabel')}>
          {hasUnverified ? (
            <div className={clsx(styles.summaryStat, styles.summaryStatPending)}>
              <strong data-testid="diag-summary-pending">{pendingChecks}</strong>
              <span>{localizeMessage('operator.diag.summary.checks_pending')}</span>
            </div>
          ) : (
            <>
              <div className={clsx(styles.summaryStat, issues.errors > 0 && styles.summaryStatError)}>
                <strong data-testid="diag-summary-errors">{issues.errors}</strong>
                <span>{localizeMessage('operator.diag.summary.errors')}</span>
              </div>
              <div className={clsx(styles.summaryStat, issues.warnings > 0 && styles.summaryStatWarning)}>
                <strong data-testid="diag-summary-warnings">{issues.warnings}</strong>
                <span>{localizeMessage('operator.diag.summary.warnings')}</span>
              </div>
            </>
          )}
        </div>
      </section>

      <div className={styles.healthGrid}>

      {/* ============================================================ */}
      {/* 3. Indexing (§4.4)                                           */}
      {/* ============================================================ */}
      <section
        className={clsx(
          styles.healthCard,
          s.indexing === IndexingStatus.READY && styles.cardHealthy,
          s.indexing === IndexingStatus.ERROR && styles.cardError,
          stuck && styles.cardWarning,
        )}
        data-testid="diag-indexing"
      >
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>
            {sevIcon(
              s.indexing === IndexingStatus.READY ? 'ok'
                : s.indexing === IndexingStatus.ERROR ? 'error'
                : stuck ? 'warning'
                : 'neutral',
            )}
          </span>
          <div>
            <p className={styles.sectionTitle}>
              {localizeMessage('operator.diag.indexing.title')}
            </p>
            <p className={styles.sectionStatus}>
              {localizeMessage(`operator.diag.indexing.${s.indexing}`)}
            </p>
          </div>
        </div>
        {s.indexing === IndexingStatus.ERROR && plugin.indexing?.error && (
          <p className={clsx(styles.sectionHint, styles.errorHint)} data-testid="diag-indexing-error">
            {plugin.indexing.error}
          </p>
        )}
        {stuck && (
          <p className={clsx(styles.sectionHint, styles.warnHint)} data-testid="diag-indexing-stuck">
            {localizeMessage('operator.diag.indexing.stuck')}
            {plugin.indexing?.updatedAt && ` (${plugin.indexing.updatedAt})`}
          </p>
        )}
        {plugin.indexing?.finishedAt && (
          <p className={styles.detailLine} data-testid="diag-indexing-last-success">
            {localizeMessage('operator.diag.indexing.last_success')}: {plugin.indexing.finishedAt}
          </p>
        )}
        {onStartReindex && s.indexing !== IndexingStatus.RUNNING && (
          <div className={styles.sectionAction}>
            <Button
              size="small"
              variant="outlined"
              onClick={onStartReindex}
              data-testid="diag-reindex-btn"
            >
              {localizeMessage('operator.diag.indexing.start_reindex')}
            </Button>
          </div>
        )}
      </section>

      {/* ============================================================ */}
      {/* 5. Preview (§4.6)                                            */}
      {/* ============================================================ */}
      <section
        className={clsx(
          styles.healthCard,
          s.previewBuild === PreviewBuildStatus.READY
            && s.previewBrowser === PreviewBrowserStatus.ENABLED && styles.cardHealthy,
          (s.previewBuild === PreviewBuildStatus.ERROR
            || s.previewBrowser === PreviewBrowserStatus.ERROR) && styles.cardError,
          s.previewBrowser === PreviewBrowserStatus.STALE && styles.cardWarning,
        )}
        data-testid="diag-preview"
      >
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>
            {sevIcon(
              s.previewBuild === PreviewBuildStatus.READY
                && s.previewBrowser === PreviewBrowserStatus.ENABLED ? 'ok'
                : s.previewBuild === PreviewBuildStatus.ERROR
                  || s.previewBrowser === PreviewBrowserStatus.ERROR ? 'error'
                : s.previewBrowser === PreviewBrowserStatus.STALE ? 'warning'
                : 'neutral',
            )}
          </span>
          <div>
            <p className={styles.sectionTitle}>
              {localizeMessage('operator.diag.preview.title')}
            </p>
            <p className={styles.sectionStatus}>
              {localizeMessage(`operator.diag.preview.build.${s.previewBuild}`)}
            </p>
          </div>
        </div>

        <div className={styles.statusRows}>
          <p className={styles.detailLine} data-testid="diag-preview-build">
            <span>{localizeMessage('operator.diag.preview.build_label')}</span>
            <strong>{localizeMessage(`operator.diag.preview.build.${s.previewBuild}`)}</strong>
          </p>
          <p className={styles.detailLine} data-testid="diag-preview-browser">
            <span>{localizeMessage('operator.diag.preview.browser_label')}</span>
            <strong>{localizeMessage(`operator.diag.preview.browser.${s.previewBrowser}`)}</strong>
          </p>
          <p className={styles.detailLine} data-testid="diag-live-status">
            <span>{localizeMessage('operator.diag.preview.live_label')}</span>
            <strong>{String(s.live).replace(/_/g, ' ')}</strong>
          </p>
        </div>

        {feedback?.has_draft_settings && (
          <p className={styles.detailLine} data-testid="diag-preview-draft">
            {localizeMessage('operator.diag.preview.has_draft')}
          </p>
        )}

        {feedback?.errors?.length > 0 && (
          <div data-testid="diag-preview-errors">
            {feedback.errors.map((err, i) => (
              <p key={i} className={styles.errorItem}>
                <ErrorIcon className={clsx(styles.iconSize, styles.iconErr)} />
                {typeof err === 'string' ? err : err.message || err.code || JSON.stringify(err)}
              </p>
            ))}
          </div>
        )}

        <div className={styles.sectionAction}>
          {s.previewBuild === PreviewBuildStatus.READY && (
            <Button
              size="small"
              variant="outlined"
              onClick={handleOpenPreview}
              disabled={!previewShopAddress}
              data-testid="diag-open-preview-btn"
            >
              {localizeMessage('operator.diag.preview.open')}
            </Button>
          )}
        </div>

        {(onRebuildBundle || onBuildAndPromote) && (
          <BundleBuildBlock
            bundleRebuildStatus={bundleRebuildStatus}
            bundleRebuildError={bundleRebuildError}
            onRebuildBundle={onRebuildBundle}
            onBuildAndPromote={onBuildAndPromote}
            shopId={shopId}
          />
        )}
      </section>
      </div>

      <div className={styles.actionsBar} data-testid="diag-actions" />
    </div>
  );
}

Diagnostics.propTypes = {
  operatorState: PropTypes.object.isRequired,
  pluginSnapshot: PropTypes.object,
  browserLocalLane: PropTypes.object,
  shopId: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  onStartReindex: PropTypes.func,
  onRebuildBundle: PropTypes.func,
  onBuildAndPromote: PropTypes.func,
  bundleRebuildStatus: PropTypes.string,
  bundleRebuildError: PropTypes.string,
};

Diagnostics.defaultProps = {
  pluginSnapshot: null,
  browserLocalLane: null,
  isLoading: false,
  onStartReindex: null,
  onRebuildBundle: null,
  onBuildAndPromote: null,
  bundleRebuildStatus: null,
  bundleRebuildError: null,
};
